// Login — email OTP request + verify. Wired to apps/api Elysia OTP routes.
// Visual ports the canon LoginScreen aesthetic (Wordmark + bg-blobs + card),
// but uses email OTP (our actual auth flow) rather than name+age (the canon
// prototype's mocked flow — name+age becomes onboarding post-auth).
//
// Per ADR-0010: 100dvh + safe-area; inputs ≥16px; touch targets ≥44pt.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { type OtpRequestResponse, StumbleApiError, authApi } from '../api'
import { Wordmark } from '../components/Wordmark'
import { identifyUser, track } from '../lib/analytics'

type Step =
  | { kind: 'request' }
  | { kind: 'verify'; email: string; expiresAt: string; devCode: string | null }
  | { kind: 'success' }

export const Route = createFileRoute('/login')({
  component: LoginRoute,
})

function LoginRoute() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>({ kind: 'request' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function handleRequest(email: string) {
    setBusy(true)
    setError(null)
    try {
      const res: OtpRequestResponse = await authApi.requestOtp(email)
      setStep({ kind: 'verify', email, expiresAt: res.expiresAt, devCode: res.devCode })
    } catch (err) {
      if (err instanceof StumbleApiError && err.status === 429) {
        setError('Too many tries. Slow down a few minutes.')
      } else {
        setError("Couldn't send the code. Try again.")
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleVerify(code: string) {
    if (step.kind !== 'verify') return
    setBusy(true)
    setError(null)
    try {
      const res = await authApi.verifyOtp(step.email, code)
      if (res.ok) {
        // CEO-5: fire `signup` then identify against /api/me so subsequent
        // events stitch to the same PostHog person. We don't know if this is
        // a brand-new account or returning login from this endpoint alone —
        // PostHog's `signup` is overloaded by convention to mean "session
        // start"; the dashboard funnel separates first-touch from repeat via
        // the `$is_identified` property.
        track('signup', { method: 'otp' })
        void authApi
          .me()
          .then((u) => {
            if (u) {
              identifyUser(u.id, {
                email: u.email ?? undefined,
                username: u.username ?? undefined,
              })
            }
          })
          .catch(() => {
            // identify is best-effort — never block navigation on it
          })
        setStep({ kind: 'success' })
        navigate({ to: '/' })
      } else if (res.reason === 'expired') {
        setError('That code expired. Tap "send again".')
      } else if (res.reason === 'rate_limited') {
        setError('Too many tries. Slow down a few minutes.')
      } else {
        setError("That code didn't match. Try again.")
      }
    } catch {
      setError("Couldn't verify the code. Try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stumble-screen no-head">
      <div className="bg-blobs" aria-hidden />
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '60px 22px 0',
          paddingTop: 'calc(env(safe-area-inset-top) + 40px)',
          // OTP input opens the soft keyboard — reserve room so the
          // submit button isn't masked. --safe-bottom tracks
          // visualViewport.bottomChrome (URL bar + home indicator + keyboard).
          paddingBottom: 'max(30px, calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 16px))',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          gap: 20,
          minHeight: '100dvh',
        }}
      >
        <div
          className="login-welcome"
          style={{
            position: 'relative',
            padding: '20px 18px 22px',
            border: '2.5px solid var(--line)',
            borderRadius: 18,
            boxShadow: '0 6px 0 var(--shadow-ink)',
            overflow: 'hidden',
            background:
              'linear-gradient(180deg, rgba(255,250,240,0.96), rgba(255,250,240,0.86)), url(/cards/pub-art.jpg) 60% 50%/cover, var(--paper)',
          }}
        >
          <Wordmark size={88} />
          <p className="body" style={{ maxWidth: '20ch', marginTop: 10 }}>
            For when "one more place" becomes three.
          </p>
        </div>

        <div className="card stack" style={{ alignContent: 'start' }}>
          {step.kind === 'request' && <RequestForm busy={busy} onSubmit={handleRequest} />}
          {step.kind === 'verify' && (
            <VerifyForm
              busy={busy}
              email={step.email}
              devCode={step.devCode}
              onSubmit={handleVerify}
              onStartOver={() => {
                setStep({ kind: 'request' })
                setError(null)
              }}
            />
          )}
          {step.kind === 'success' && <p className="body">Signing you in…</p>}
          {error && (
            <p
              role="alert"
              data-testid="login-error"
              style={{ color: 'var(--danger)', fontWeight: 800, marginTop: 8 }}
            >
              {error}
            </p>
          )}
        </div>

        <div className="muted body-sm" style={{ textAlign: 'center', fontSize: 11 }}>
          By signing in, you agree to drink responsibly. Stumble doesn't make you stumble — you do.
        </div>
      </div>
    </div>
  )
}

function RequestForm({ busy, onSubmit }: { busy: boolean; onSubmit: (email: string) => void }) {
  const [email, setEmail] = useState('')
  const canSubmit = isEmailish(email) && !busy
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) onSubmit(email.trim())
      }}
      style={{ display: 'grid', gap: 14 }}
    >
      <label htmlFor="login-email" className="eyebrow">
        your email
      </label>
      <input
        id="login-email"
        data-testid="login-email"
        type="email"
        autoComplete="email"
        inputMode="email"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@stumble.party"
        style={{
          minHeight: 56,
          padding: '0 14px',
          border: '2px solid var(--line)',
          borderRadius: 12,
          background: 'var(--paper-warm)',
          fontWeight: 800,
          fontSize: 16,
          width: '100%',
          boxSizing: 'border-box',
        }}
        required
      />
      <button
        type="submit"
        data-testid="login-submit"
        disabled={!canSubmit}
        className="btn pink full"
      >
        {busy ? 'Sending…' : 'Send sign-in code'}
      </button>
    </form>
  )
}

function VerifyForm({
  busy,
  email,
  devCode,
  onSubmit,
  onStartOver,
}: {
  busy: boolean
  email: string
  devCode: string | null
  onSubmit: (code: string) => void
  onStartOver: () => void
}) {
  const [code, setCode] = useState('')
  const canSubmit = /^[0-9]{6}$/.test(code) && !busy
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) onSubmit(code)
      }}
      style={{ display: 'grid', gap: 14 }}
    >
      <p className="eyebrow">code sent to {email}</p>
      <input
        id="login-code"
        data-testid="login-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
        placeholder="123456"
        style={{
          minHeight: 56,
          padding: '0 14px',
          border: '2px solid var(--line)',
          borderRadius: 12,
          background: 'var(--paper-warm)',
          fontWeight: 800,
          fontSize: 22,
          letterSpacing: '0.5em',
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box',
        }}
        required
      />
      <button
        type="submit"
        data-testid="login-verify"
        disabled={!canSubmit}
        className="btn pink full"
      >
        {busy ? 'Checking…' : 'Tap in'}
      </button>
      <button
        type="button"
        onClick={onStartOver}
        className="btn ghost"
        data-testid="login-restart"
        style={{ minHeight: 44 }}
      >
        send again / change email
      </button>
      {devCode && (
        <p
          data-testid="login-dev-code"
          className="mono muted"
          style={{ fontSize: 12, textAlign: 'center' }}
        >
          dev: {devCode}
        </p>
      )}
    </form>
  )
}

function isEmailish(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim())
}
